import { useCallback, useMemo } from 'react';

import { useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { updateTaxResidences } from '@/features/taxResidences/store/taxResidence.actions';

import { selectProfileByUserId, selectTaxResidencesByUserId } from '@/store/selectors';
import { ITaxResidenceUpdate } from '@/store/models/taxResidence.model';

import { ICreateUserInfo, ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';
import { IPutUser, IUser } from '@/features/user/store/user.model';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';
import { updateUser } from '@/features/user/store/user.actions';

type TPutUser = IPutUser & {
  taxResidences?: ITaxResidenceCreate[];
};

export function useUpdateTaxResidences(user: IUser) {
  const dispatch = useAppDispatch();

  const isUserLoading = useAppSelector((state) => selectProfileByUserId(state, user.userId)?.state?.user.isLoading);

  const currentTaxResidences = useAppSelector((state) => selectTaxResidencesByUserId(state, user?.userId));

  const { isTaxResidenceDisabled, isTaxResidenceCreated, isTaxResidenceLoading } = useAddTaxResidences();

  const defaultValues: ICreateUserInfo = useMemo(() => {
    return {
      ...user,
      taxResidences: currentTaxResidences,
    };
  }, [user, currentTaxResidences]);

  const {
    reset,
    watch,
    setValue,
    formState: { touchedFields },
  } = useForm<ICreateUserInfo>({ defaultValues });

  const handleValueChange = useCallback(
    (value: string | number | boolean | ITaxResidenceCreate[], field: keyof ICreateUserInfo) => setValue(field, value),
    [setValue],
  );

  const handlePrefillData = useCallback(
    (data: IUser) => {
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value !== undefined) {
          handleValueChange(value, key as keyof TPutUser);
        }
      });
    },
    [handleValueChange],
  );

  const handleUpdateTaxResidences = (onFinish?: () => void) => {
    const { taxResidences, specifiedUSPerson } = watch();

    if (specifiedUSPerson !== user.specifiedUSPerson) {
      dispatch(updateUser({ specifiedUSPerson }, user.userId));
    }

    const mapNewTaxResidences = new Map(
      taxResidences.filter((taxResidence) => taxResidence.id).map((taxResidence) => [taxResidence.id!, taxResidence]),
    );

    const updatedTaxResidences: ITaxResidenceUpdate[] = [];

    for (const ancien of currentTaxResidences) {
      const newTaxResidence = mapNewTaxResidences.get(ancien.id!);
      if (!newTaxResidence) {
        updatedTaxResidences.push({ ...ancien, isDeleted: true });
      } else if (JSON.stringify(ancien) !== JSON.stringify(newTaxResidence)) {
        updatedTaxResidences.push({ ...newTaxResidence, isUpdated: true });
      }
    }
    const newTaxResidences: ITaxResidenceUpdate[] = taxResidences.filter((p) => !p.id);

    const taxResidencesToUpdate: ITaxResidenceUpdate[] = [...updatedTaxResidences, ...newTaxResidences].map(
      (values) => ({ ...values, userId: user.userId }),
    );

    dispatch(updateTaxResidences(user.userId, taxResidencesToUpdate, onFinish));
  };

  const shouldBeUSResident = useCallback(
    (declarativeData: (keyof IPutUser)[]) =>
      declarativeData.some((key: keyof IPutUser) => user?.[key] === 'US' || watch(key) === 'US'),
    [watch, user],
  );

  const onCancelEdition = useCallback(
    (toogleSectionEditMode: () => void) => {
      reset();
      toogleSectionEditMode();
    },
    [reset],
  );

  return {
    handleUpdateTaxResidences,
    watch,
    touchedFields,
    handlePrefillData,
    handleValueChange,
    shouldBeUSResident,
    onCancelEdition,
    isLoading: isUserLoading || isTaxResidenceLoading,
    isTaxResidenceDisabled,
    isTaxResidenceCreated,
  };
}
